/**
 * PIN lock: PBKDF2-SHA256 over a random salt.
 *
 * Decisions (docs/DECISIONS.md B3): no auto-lock timer, but high-risk approvals
 * re-verify the PIN. Passkey/WebAuthn is an interface placeholder for M1 — the
 * UI shows "预留接口" instead of pretending it works.
 */
import { base64UrlDecode, base64UrlEncode, randomBytes } from './id';
import { sessionGet, sessionRemove, sessionSet } from './storage';

export interface PinRecord {
  version: 1;
  salt: string;
  iterations: number;
  hash: string;
}

export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 8;
export const PIN_ITERATIONS = 120_000;
const UNLOCK_FLAG = 'unlocked';

export function isValidPin(pin: string): boolean {
  return new RegExp(`^[0-9]{${PIN_MIN_LENGTH},${PIN_MAX_LENGTH}}$`, 'u').test(pin);
}

async function derivePinBits(
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as unknown as BufferSource, iterations },
    keyMaterial,
    256,
  );
  return new Uint8Array(bits);
}

export async function hashPin(
  pin: string,
  options: { salt?: Uint8Array; iterations?: number } = {},
): Promise<PinRecord> {
  if (!isValidPin(pin)) throw new Error(`PIN must be ${PIN_MIN_LENGTH}-${PIN_MAX_LENGTH} digits`);
  const salt = options.salt ?? randomBytes(16);
  const iterations = options.iterations ?? PIN_ITERATIONS;
  const hash = await derivePinBits(pin, salt, iterations);
  return {
    version: 1,
    salt: base64UrlEncode(salt),
    iterations,
    hash: base64UrlEncode(hash),
  };
}

export async function verifyPin(pin: string, record: PinRecord): Promise<boolean> {
  if (!isValidPin(pin)) return false;
  try {
    const salt = base64UrlDecode(record.salt);
    const expected = base64UrlDecode(record.hash);
    const actual = await derivePinBits(pin, salt, record.iterations);
    if (actual.length !== expected.length) return false;
    let diff = 0;
    for (let index = 0; index < actual.length; index += 1) {
      diff |= (actual[index] ?? 0) ^ (expected[index] ?? 0);
    }
    return diff === 0;
  } catch {
    return false;
  }
}

/** In-memory (per browser session) unlock flag: no auto-lock timer by decision. */
export const unlockSession = {
  isUnlocked(): boolean {
    return sessionGet(UNLOCK_FLAG) === '1';
  },
  markUnlocked(): void {
    sessionSet(UNLOCK_FLAG, '1');
  },
  clear(): void {
    sessionRemove(UNLOCK_FLAG);
  },
};

/**
 * Passkey / WebAuthn provider interface (M1 placeholder).
 *
 * The real implementation will create a credential during pairing and require
 * userVerification for high-risk approvals. For M0 it reports unsupported so
 * callers can fall back to PIN instead of silently skipping verification.
 */
export interface PasskeyProvider {
  readonly id: 'webauthn';
  supported(): Promise<boolean>;
  authenticate(reason: string): Promise<{ ok: boolean; error?: string }>;
}

export const passkeyProvider: PasskeyProvider = {
  id: 'webauthn',
  async supported(): Promise<boolean> {
    return typeof PublicKeyCredential !== 'undefined';
  },
  async authenticate(): Promise<{ ok: boolean; error?: string }> {
    return {
      ok: false,
      error: 'Passkey 尚未绑定（M1 预留接口），请使用 PIN 或一次性确认',
    };
  },
};

export function pinRecordFrom(value: unknown): PinRecord | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;
  if (
    candidate.version !== 1 ||
    typeof candidate.salt !== 'string' ||
    typeof candidate.iterations !== 'number' ||
    typeof candidate.hash !== 'string'
  ) {
    return null;
  }
  return {
    version: 1,
    salt: candidate.salt,
    iterations: candidate.iterations,
    hash: candidate.hash,
  };
}
